// app/javascript/controllers/pages_controllers/mobile_toc_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "button", "progressCircle", "item", "indicator", "link"]
  
  connect() {
    console.log("Mobile TOC controller connected")
    this.isOpen = false
    this.setupScrollListener()
    this.initializeFallbackMapping()
    
    // Calculate the circle circumference
    const circle = this.progressCircleTarget
    this.circumference = 2 * Math.PI * parseFloat(circle.getAttribute('r'))
    circle.style.strokeDasharray = this.circumference
    
    // Initial progress update and active section
    this.updateProgress()
    this.updateActiveSection()
  }
  
  disconnect() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.boundUpdateProgress)
    window.removeEventListener('scroll', this.boundUpdateActiveSection)
    
    // Clean up body styles if panel was left open
    document.body.style.overflow = ''
    document.body.style.touchAction = ''
  }
  
  setupScrollListener() {
    // Bind the methods to the instance
    this.boundUpdateProgress = this.updateProgress.bind(this)
    this.boundUpdateActiveSection = this.updateActiveSection.bind(this)
    
    // Add scroll event listeners
    window.addEventListener('scroll', this.boundUpdateProgress)
    window.addEventListener('scroll', this.boundUpdateActiveSection)
  }

  /**
   * Initialize fallback mapping for TOC items to H2 headings.
   * This creates a mapping by order when UUID-based IDs don't match.
   * Includes rich_text H2s and FAQ sections in order of appearance.
   */
  initializeFallbackMapping() {
    // Find all H2 headings in rich_text components (they have id="rich-text-heading-...")
    // and FAQ sections (they have id="faq-heading-...")
    const allH2s = Array.from(document.querySelectorAll('h2[id^="rich-text-heading-"], h2[id^="faq-heading-"]'))
    
    // Store the fallback mapping: TOC index -> H2 element
    this.fallbackMapping = {}
    
    this.itemTargets.forEach((item, index) => {
      const targetId = item.dataset.itemId
      const element = document.getElementById(targetId)
      
      if (!element && allH2s[index]) {
        // If the intended element doesn't exist, map this TOC item to the H2 at the same index
        this.fallbackMapping[index] = allH2s[index]
        console.log(`Mobile TOC Fallback: Item ${index} ("${item.textContent.trim()}") -> H2 "${allH2s[index].textContent.trim()}"`)
      }
    })
    
    if (Object.keys(this.fallbackMapping).length > 0) {
      console.warn(`Mobile TOC: Using fallback mapping for ${Object.keys(this.fallbackMapping).length} items due to UUID mismatch`)
    }
  }

  /**
   * Get the target element for a TOC item, using fallback if needed.
   * @param {number} index - The TOC item index
   * @param {string} targetId - The intended target ID
   * @returns {HTMLElement|null} - The target element
   */
  getTargetElement(index, targetId) {
    const element = document.getElementById(targetId)
    if (element) {
      return element
    }
    
    // Use fallback mapping if available
    if (this.fallbackMapping[index]) {
      return this.fallbackMapping[index]
    }
    
    return null
  }
  
  toggle(event) {
    if (event) {
      event.preventDefault()
    }
    
    this.isOpen = !this.isOpen
    
    if (this.isOpen) {
      // Use a simpler slide-up animation instead of radial expansion
      this.panelTarget.classList.remove('opacity-0', 'pointer-events-none', 'translate-y-full')
      this.panelTarget.classList.add('opacity-100', 'translate-y-0')
      this.buttonTarget.classList.add('opacity-0', 'pointer-events-none')
      
      // Prevent body scroll when panel is open (mobile only)
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      // Slide down animation
      this.panelTarget.classList.remove('opacity-100', 'translate-y-0')
      this.panelTarget.classList.add('opacity-0', 'pointer-events-none', 'translate-y-full')
      this.buttonTarget.classList.remove('opacity-0', 'pointer-events-none')
      
      // Re-enable body scroll
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }
  
  updateProgress() {
    // Calculate scroll progress
    const scrollTop = window.scrollY
    const windowHeight = window.innerHeight
    const docHeight = document.documentElement.scrollHeight
    
    let scrolled = (scrollTop / (docHeight - windowHeight))
    scrolled = Math.min(Math.max(scrolled, 0), 1) // Clamp between 0 and 1
    
    // Update circle progress
    const circle = this.progressCircleTarget
    const dashoffset = this.circumference * (1 - scrolled)
    circle.style.strokeDashoffset = dashoffset
  }
  
  updateActiveSection() {
    // Find which section is currently in view
    const scrollPosition = window.scrollY + 100
    
    // Get all sections using both direct ID lookup and fallback mapping
    const sections = this.itemTargets.map((item, index) => {
      const targetId = item.dataset.itemId
      return this.getTargetElement(index, targetId)
    }).filter(Boolean)
    
    let activeElement = null
    
    // Find the current section
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i]
      const nextSection = sections[i + 1]
      
      const sectionTop = section.offsetTop
      const sectionBottom = nextSection ? nextSection.offsetTop : document.documentElement.scrollHeight
      
      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        activeElement = section
        break
      }
    }
    
    // Update the active state
    this.setActiveItem(activeElement)
  }
  
  setActiveItem(activeElement) {
    // Reset all items
    this.itemTargets.forEach((item, index) => {
      const link = this.linkTargets[index]
      const indicator = this.indicatorTargets[index]
      
      // Reset styling
      link.classList.remove("font-semibold", "text-ph24_blue")
      link.classList.add("text-gray-600")
      
      if (indicator) {
        indicator.style.backgroundColor = "transparent"
      }
    })
    
    // Style active item only if there's an active element
    if (activeElement) {
      // Find which TOC item corresponds to this element
      const activeIndex = this.itemTargets.findIndex((item, index) => {
        const targetId = item.dataset.itemId
        const element = this.getTargetElement(index, targetId)
        return element === activeElement
      })
      
      if (activeIndex >= 0) {
        const activeLink = this.linkTargets[activeIndex]
        const activeIndicator = this.indicatorTargets[activeIndex]
        
        // Set active styles
        activeLink.classList.remove("text-gray-600")
        activeLink.classList.add("font-semibold", "text-ph24_blue")
        
        if (activeIndicator) {
          activeIndicator.style.backgroundColor = "#12273e" // ph24_blue
        }
      }
    }
  }
  
  scrollToSectionAndClose(event) {
    event.preventDefault()
    
    const href = event.currentTarget.getAttribute("href")
    const id = href.substring(1) // Remove the # from the href
    
    // First try direct ID lookup
    let element = document.getElementById(id)
    
    // If not found, use fallback mapping
    if (!element) {
      const tocIndex = this.linkTargets.findIndex(link => link.getAttribute("href") === href)
      if (tocIndex >= 0 && this.fallbackMapping[tocIndex]) {
        element = this.fallbackMapping[tocIndex]
        console.log(`Mobile TOC: Using fallback for scroll to "${this.itemTargets[tocIndex].textContent.trim()}"`)
      }
    }
    
    // Close the panel
    this.toggle()
    
    // After panel closes, scroll to section
    setTimeout(() => {
      if (element) {
        const yOffset = -80 // Adjust based on your fixed header height
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }, 300) // Reduced wait time for smoother experience
  }
}